


















































































































































































// Import vendors ----------------------------------------------------------------------------------
import {
  defineComponent,
  ref,
  reactive,
  computed,
  watch,
  toRefs,
  Ref,
  PropType,
  onMounted
} from '@vue/composition-api';
// Import components -------------------------------------------------------------------------------
import ElementAnalysisFilter from '@/components/elements/ElementAnalysisFilter.vue';
// Import plugins ----------------------------------------------------------------------------------
import { usePodocoreModule } from '@/plugins/podocore';
import { usePodocoreModuleService } from '@/plugins/podocore';
// Import helpers ----------------------------------------------------------------------------------
import { downloadPdf } from '@/helpers/helped-functions.helper';
import { dateFormatRelatedToSelectedLanguage } from '@/helpers/basic-values.helper';
// Import utils ------------------------------------------------------------------------------------
import { useRoute } from '@/utils/router.utils';
import { useCDN } from '@/utils/cdn.utils';
import { useRehab } from '@/utils/rehab.utils';
import { getIconByScenario } from '@/utils/icon.utils';
import { useLocalStorage } from '@vueuse/core';
import { useSingleAnalysis } from '@/utils/single-analysis.utils';
// Import types ------------------------------------------------------------------------------------
import type { Entity } from '@/plugins/podocore/helpers/repositories.helper';
import type { Patient } from '@/plugins/podocore/repositories/patients.repository';
import { AnalysisType } from '@/plugins/podocore/repositories/analysis.repository';
import { MediaType } from '@/utils/cdn.utils';
import { SupportedLang } from '@/utils/i18n.utils';
import type { AnalysisEntityCompleted } from '@digitsole/blackburn-entities/dist/entities/analysis/analysis-completed.entity';
// -------------------------------------------------------------------------------------------------

export enum EAnalysisName {
  Walking = 'walk',
  Running = 'run',
  Jumping = 'jumping'
}
export enum EAnalysisType {
  Walking = 'walking',
  Running = 'running',
  Jumping = 'jumping'
}
export enum EFilter {
  Default = 'default',
  Neurological = 'neurological',
  Traumatology = 'traumatology',
  Reathleticism = 're-athleticism',
  Custom = 'custom'
}
export type TAnalysisNames = {
  [AnalysisType.walking]: EAnalysisName.Walking;
  [AnalysisType.running]: EAnalysisName.Running;
  [AnalysisType.jumping]: EAnalysisName.Jumping;
};
export type TAnalysisType = {
  [AnalysisType.walking]: EAnalysisType.Walking;
  [AnalysisType.running]: EAnalysisType.Running;
  [AnalysisType.jumping]: EAnalysisType.Jumping;
};

export enum EResultsTab {
  General = 'general',
  Advanced = 'advanced'
}

export default defineComponent({
  name: 'PatientResultsHeader',
  components: {
    //ElementAnalysisFilter
  },
  props: {
    analysis: {
      type: Object as PropType<AnalysisEntityCompleted>,
      required: true
    },
    scenario: {
      type: Object,
      required: true
    },
    patient: {
      type: Object as PropType<Entity<Patient>>,
      required: true
    },
    degradedAnalysis: {
      type: Boolean
    }
  },
  setup(properties, { root, emit }) {
    const { analysis } = toRefs(properties);
    const { getMode } = useRehab();
    const formatedDate = dateFormatRelatedToSelectedLanguage();
    const { setSingleAnalysis, singleAnalysis } = useSingleAnalysis();

    const analysisType = properties.scenario.key;

    const nameByAnalysisType: TAnalysisNames = reactive({
      [AnalysisType.walking]: EAnalysisName.Walking,
      [AnalysisType.running]: EAnalysisName.Running,
      [AnalysisType.jumping]: EAnalysisName.Jumping
    });
    const refactoredAnalysisType: TAnalysisType = reactive({
      [AnalysisType.walking]: EAnalysisType.Walking,
      [AnalysisType.running]: EAnalysisType.Running,
      [AnalysisType.jumping]: EAnalysisType.Jumping
    });

    const { query } = useRoute();
    const { cdn } = useCDN();
    const busModule = usePodocoreModule('bus');

    const isWalking = computed(() => analysisType.startsWith('walking'));
    const isRunning = computed(() => analysisType.startsWith('running'));
    const isRehab = computed(() => analysisType.startsWith('jumping'));

    const haveScoring = computed(
      () =>
        (analysis.value.display_metrics as any).aggregates.walking_gait_variability_index &&
        (analysis.value.display_metrics as any).aggregates.walking_SQI
    );

    const selectedFilter = ref('default');

    // Type of view
    const isDefaultView: Ref<boolean> = useLocalStorage('isDefaultView', true);
    function setSelectedFilter(_filter: string) {
      selectedFilter.value = _filter;
      emit('setSelectedFilter', _filter);
      emit('setScoringView', false);
    }
    function setDefaultView(defaultView: boolean) {
      emit('setScoringView', false);
      isDefaultView.value = defaultView;
    }

    // Running exported segments selection
    const exportSelectedSegments = ref(null);

    // Steps
    const resultsTab = ref(EResultsTab.General);
    const resultsTabLocal = useLocalStorage('results-tab', EResultsTab.General);

    const steps = computed(() => {
      if (isRunning.value) {
        return [
          {
            id: 'general',
            text: root.$tc('commons.standards.general', 1) as string
          },
          {
            id: 'advanced',
            text: root.$tc('commons.standards.detail', 2) as string
          }
        ];
      }

      return [
        {
          id: 'general',
          text: root.$tc('commons.standards.general', 1) as string
        },
        {
          id: 'advanced',
          text: root.$tc('commons.standards.detail', 2) as string
        }
      ];
    });
    watch(resultsTab, (_tab) => {
      resultsTabLocal.value = resultsTab.value;
      emit('setScoringView', false);
      emit('setResultsTab', _tab);
      exportSelectedSegments.value = null;
    });

    // Interpretation manual
    const analysisTypeCode = computed(() => (isWalking ? '010' : '020'));
    const manual = computed(() => {
      return {
        url: cdn(
          MediaType.Documents,
          `PS_0001_IFU_${analysisTypeCode.value}_REV02_${currentLang.value.toUpperCase()}.pdf`
        ),
        fileName: `interpretation-manual-${analysisType}`
      };
    });

    // Current lang
    const doctorService = usePodocoreModuleService('doctor');
    const currentLang = computed<SupportedLang>(() => {
      const lang = (doctorService?.state.value as any).context.doctor.config.lang;
      return lang === SupportedLang.En || lang === SupportedLang.Fr ? lang : SupportedLang.En;
    });

    // Rename
    function openRenameAnalysis(cuid: string) {
      busModule.publish(
        busModule.events.openDialog({
          id: 'analysis-rename',
          meta: { analysisCuid: cuid }
        })
      );
    }

    // Comparison
    function setScoringView(status: boolean) {
      emit('setScoringView', status);
    }
    // Comparison
    function openComparison() {
      if (query.value?.firstAnalysis != analysis.value.cuid) {
        root.$router.push({
          name: 'patient-comparison',
          query: {
            firstAnalysis: analysis.value.cuid
          }
        });
      }
    }
    // PDF export
    function openExport() {
      if (analysis.value) {
        busModule.publish(
          busModule.events.openDialog({
            id: 'analysis-export-pdf',
            meta: {
              resultsTab: resultsTab.value,
              scenario: properties.analysis._scenario?.key
                ? properties.analysis._scenario.key
                : properties.scenario.key,
              analysis: singleAnalysis.value,
              patient: properties.patient,
              mode: 'default',
              exportSelectedSegments: exportSelectedSegments.value,
              filter: singleAnalysis.value.filter
            }
          })
        );
      }
    }
    // Delete analysis
    function openDelete() {
      busModule.publish(
        busModule.events.openDialog({
          id: 'analysis-delete',
          meta: { analysisCuid: analysis.value.cuid }
        })
      );
    }

    busModule.useEventSubscriber(busModule.events.activityRunningSelectedSegments, (event) => {
      if (event?.payload?.selectedSegments) exportSelectedSegments.value = event.payload.selectedSegments;
    });

    busModule.useEventSubscriber(busModule.events.analysisRenamed, (event) => {
      setSingleAnalysis({ ...singleAnalysis.value, name: event.payload.name });
    });

    onMounted(() => {
      setSingleAnalysis(analysis.value);
      if (!analysis.value.filter) {
        setSelectedFilter('default');
        setDefaultView(true);
      }
      if (analysis.value.filter) selectedFilter.value = analysis.value.filter;
      if (!isWalking.value || (isDefaultView.value && selectedFilter.value !== 'default')) {
        setDefaultView(true);
        setSelectedFilter('default');
      }
    });

    return {
      singleAnalysis,
      // Values
      formatedDate,
      nameByAnalysisType,
      refactoredAnalysisType,
      analysisType,
      resultsTab,
      selectedFilter,
      steps,
      manual,
      // Flags
      isWalking,
      isRunning,
      isRehab,
      haveScoring,
      isDefaultView,
      // Methods
      openRenameAnalysis,
      setScoringView,
      openComparison,
      openExport,
      openDelete,
      getMode,
      // Emit
      setSelectedFilter,
      setDefaultView,
      // Helpers
      downloadPdf,
      // Utils
      getIconByScenario
    };
  }
});
